import { useState } from "react"
import { Head, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card } from "@/components/ui/card"
import { ArrowRight, Eye, EyeOff } from "lucide-react"
import logo from "@/assets/logo-25segundos-horizontal.png"

interface Props {
  flash?: {
    notice?: string
    alert?: string
  }
  errors?: {
    base?: string
    email?: string[]
    password?: string[]
  }
}

export default function Login({ flash, errors }: Props) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    router.post("/login", { email, password }, {
      onFinish: () => setIsLoading(false)
    })
  }

  return (
    <>
      <Head title="Login - Orientação Profissional" />

      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md p-8 shadow-elegant">
          <div className="space-y-6">
            {/* Link para cadastro */}
            <div className="flex justify-end -mb-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-primary hover:underline p-0 h-auto"
                onClick={() => router.visit("/auth-flow")}
              >
                Não sou cadastrado? <span className="font-semibold ml-1">Criar conta</span>
              </Button>
            </div>

            {/* Logo */}
            <div className="flex justify-center">
              <img src={logo} alt="25Segundos" className="h-16" />
            </div>

            {/* Titulo */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-title-blue">Orientação Profissional</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Faça login para continuar
              </p>
            </div>

            {/* Alertas */}
            {flash?.notice && (
              <div className="p-4 bg-green-100 text-green-800 rounded-lg text-sm">
                {flash.notice}
              </div>
            )}

            {flash?.alert && (
              <div className="p-4 bg-red-100 text-red-800 rounded-lg text-sm">
                {flash.alert}
              </div>
            )}

            {errors?.base && (
              <div className="p-4 bg-red-100 text-red-800 rounded-lg text-sm">
                {errors.base}
              </div>
            )}

            {/* Formulario */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
                {errors?.email && (
                  <p className="text-sm text-red-500">{errors.email[0]}</p>
                )}
              </div>

              {/* Senha */}
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors?.password && (
                  <p className="text-sm text-red-500">{errors.password[0]}</p>
                )}
              </div>

              {/* Botao submit */}
              <Button
                type="submit"
                className="w-full bg-gradient-primary hover:opacity-90 transition-opacity"
                disabled={isLoading}
              >
                {isLoading ? "Entrando..." : "Entrar"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              {/* Esqueci senha */}
              <button
                type="button"
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors underline"
                onClick={() => router.visit("/esqueci-senha")}
              >
                Esqueci minha senha
              </button>
            </form>
          </div>
        </Card>
      </div>
    </>
  )
}
